import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { useGetOrgRolesQuery } from '@/store/query/roles';
import { INTERNAL_EMAIL_ON_JOB_ROLES } from '@/constants/on-job-roles';
import { orgRoleToken } from '@/utils/internalEmailRecipients';
import type { EmailUser } from '@/components/shared/email/emailValue';

interface OrgRole {
  id: number | string;
  name: string;
}

/**
 * Recipients an internal email can be addressed to: teammates individually,
 * by their role on the job, or by their Kula role.
 *
 * The three are returned as one flat list rather than three labelled sections,
 * because kula's `Email` manages its own recipient `Select` options and accepts
 * only a flat `SelectOption[]` (groups are supported for the template row only).
 * Role entries are labelled so they still read as roles — "(on job)" for a job
 * role, "(on Kula)" for an org role — which is how the selected pills are drawn
 * in the design. Restoring the sectioned dropdown needs `toOptions`/
 * `ccBccOptions` in `@kula-ai/components` widened to accept `SelectGroup`, the
 * way `templateOptions` already is.
 */
export const useInternalEmailRecipientOptions = () => {
  const users = useSelector(selectUsers) as EmailUser[] | undefined;
  const { data, isLoading } = useGetOrgRolesQuery({});

  const orgRoles = useMemo(
    () => ((data as { org_roles?: OrgRole[] })?.org_roles ?? []) as OrgRole[],
    [data]
  );

  /**
   * Shaped as `EmailUser` so the composer's own option builder and value
   * resolver treat roles exactly like people; `hideAvatar` keeps a role from
   * rendering an initials bubble.
   */
  const recipientUsers = useMemo<EmailUser[]>(
    () => [
      ...INTERNAL_EMAIL_ON_JOB_ROLES.map((role) => ({
        id: role.value,
        name: role.label,
        hideAvatar: true,
      })),
      ...orgRoles.map((role) => ({
        id: orgRoleToken(role.id),
        name: `${role.name} (on Kula)`,
        hideAvatar: true,
      })),
      ...(users ?? []),
    ],
    [orgRoles, users]
  ) as EmailUser[];

  return { recipientUsers, isLoading };
};
